import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../../contexts/DarkModeContext';
import './RewardsSystem.css';
import { 
    FaTrophy, FaStar, FaMedal, FaCrown, FaGem, 
    FaFire, FaRocket, FaLightbulb, FaGraduationCap,
    FaBook, FaCalculator, FaChartLine, FaAward
} from 'react-icons/fa';

const RewardsSystem = () => {
    const { isDarkMode } = useDarkMode();
    const [points, setPoints] = useState(parseInt(localStorage.getItem('total-points') || '0'));
    const [level, setLevel] = useState(parseInt(localStorage.getItem('total-level') || '1'));
    const [achievements, setAchievements] = useState(JSON.parse(localStorage.getItem('achievements') || '[]'));
    const [showReward, setShowReward] = useState(false);
    const [rewardMessage, setRewardMessage] = useState('');

    // تعريف الإنجازات
    const allAchievements = [
        {
            id: 'first_calculation',
            title: 'أول عملية حسابية',
            description: 'أكمل أول عملية حسابية',
            iconType: 'calculator',
            points: 50,
            condition: (stats) => stats.calculations >= 1
        },
        {
            id: 'math_beginner',
            title: 'مبتدئ الرياضيات',
            description: 'أكمل 10 عمليات حسابية',
            iconType: 'book',
            points: 100,
            condition: (stats) => stats.calculations >= 10
        },
        {
            id: 'math_intermediate',
            title: 'متوسط الرياضيات',
            description: 'أكمل 50 عملية حسابية',
            iconType: 'lightbulb',
            points: 200,
            condition: (stats) => stats.calculations >= 50
        },
        {
            id: 'math_expert',
            title: 'خبير الرياضيات',
            description: 'أكمل 100 عملية حسابية',
            iconType: 'graduation',
            points: 500,
            condition: (stats) => stats.calculations >= 100
        },
        {
            id: 'level_5',
            title: 'المستوى الخامس',
            description: 'وصل للمستوى الخامس',
            iconType: 'star',
            points: 300,
            condition: (stats) => stats.level >= 5
        },
        {
            id: 'level_10',
            title: 'المستوى العاشر',
            description: 'وصل للمستوى العاشر',
            iconType: 'medal',
            points: 600,
            condition: (stats) => stats.level >= 10
        },
        {
            id: 'level_20',
            title: 'المستوى العشرين',
            description: 'وصل للمستوى العشرين',
            iconType: 'trophy',
            points: 1000,
            condition: (stats) => stats.level >= 20
        },
        {
            id: 'points_500',
            title: '500 نقطة',
            description: 'اجمع 500 نقطة',
            iconType: 'gem',
            points: 100,
            condition: (stats) => stats.points >= 500
        },
        {
            id: 'points_1000',
            title: '1000 نقطة',
            description: 'اجمع 1000 نقطة',
            iconType: 'crown',
            points: 200,
            condition: (stats) => stats.points >= 1000
        },
        {
            id: 'daily_streak_3',
            title: '3 أيام متتالية',
            description: 'استخدم الحاسبة 3 أيام متتالية',
            iconType: 'fire',
            points: 150,
            condition: (stats) => stats.dailyStreak >= 3
        },
        {
            id: 'daily_streak_7',
            title: 'أسبوع من التحدي',
            description: 'استخدم الحاسبة 7 أيام متتالية',
            iconType: 'rocket',
            points: 400,
            condition: (stats) => stats.dailyStreak >= 7
        },
        {
            id: 'speed_demon',
            title: 'سريع كالبرق',
            description: 'أكمل 10 عمليات في أقل من دقيقة',
            iconType: 'chart',
            points: 250,
            condition: (stats) => stats.fastCalculations >= 10
        }
    ];

    // الحصول على الإحصائيات
    const getStats = () => {
        const calculatorPoints = parseInt(localStorage.getItem('calculator-points') || '0');
        const calculatorLevel = parseInt(localStorage.getItem('calculator-level') || '1');
        const calculations = parseInt(localStorage.getItem('calculator-calculations') || '0');
        const dailyStreak = parseInt(localStorage.getItem('daily-streak') || '0');
        const fastCalculations = parseInt(localStorage.getItem('fast-calculations') || '0');

        return {
            points: calculatorPoints,
            level: calculatorLevel,
            calculations,
            dailyStreak,
            fastCalculations
        };
    };

    // فحص الإنجازات الجديدة
    const checkAchievements = () => {
        const stats = getStats();
        const newAchievements = [];

        allAchievements.forEach(achievement => {
            const isUnlocked = achievement.condition(stats);
            const alreadyUnlocked = achievements.some(a => a.id === achievement.id);

            if (isUnlocked && !alreadyUnlocked) {
                newAchievements.push(achievement);
            }
        });

        if (newAchievements.length > 0) {
            newAchievements.forEach(achievement => {
                // حفظ الإنجاز بدون الأيقونة (فقط البيانات القابلة للتحويل إلى JSON)
                const achievementData = {
                    id: achievement.id,
                    title: achievement.title,
                    description: achievement.description,
                    iconType: achievement.iconType,
                    points: achievement.points
                };
                
                const newAchievementsList = [...achievements, achievementData];
                setAchievements(newAchievementsList);
                localStorage.setItem('achievements', JSON.stringify(newAchievementsList));
                
                const newPoints = points + achievement.points;
                setPoints(newPoints);
                localStorage.setItem('total-points', newPoints.toString());
                
                // عرض رسالة الإنجاز
                setRewardMessage(`🎉 مبروك! لقد حصلت على إنجاز: ${achievement.title}`);
                setShowReward(true);
                
                setTimeout(() => {
                    setShowReward(false);
                }, 3000);
            });
        }
    };

    useEffect(() => {
        checkAchievements();
    }, [points, level]);

    const getLevelTitle = (level) => {
        if (level <= 5) return 'مبتدئ';
        if (level <= 10) return 'متوسط';
        if (level <= 15) return 'متقدم';
        if (level <= 20) return 'خبير';
        return 'أسطورة';
    };

    const getLevelIcon = (level) => {
        if (level <= 5) return <FaStar />;
        if (level <= 10) return <FaMedal />;
        if (level <= 15) return <FaTrophy />;
        if (level <= 20) return <FaCrown />;
        return <FaGem />;
    };

    const getAchievementIcon = (iconType) => {
        switch (iconType) {
            case 'calculator': return <FaCalculator />;
            case 'book': return <FaBook />;
            case 'lightbulb': return <FaLightbulb />;
            case 'graduation': return <FaGraduationCap />;
            case 'star': return <FaStar />;
            case 'medal': return <FaMedal />;
            case 'trophy': return <FaTrophy />;
            case 'gem': return <FaGem />;
            case 'crown': return <FaCrown />;
            case 'fire': return <FaFire />;
            case 'rocket': return <FaRocket />;
            case 'chart': return <FaChartLine />;
            default: return <FaCalculator />;
        }
    };

    const stats = getStats();
    const nextLevelPoints = level * 100;
    const progress = ((points % 100) / 100) * 100;

    return (
        <div className={`rewards-container ${isDarkMode ? 'dark' : 'light'}`}>
            {showReward && (
                <div className="reward-notification">
                    <div className="reward-content">
                        <FaAward className="reward-icon" />
                        <span>{rewardMessage}</span>
                    </div>
                </div>
            )}

            <div className="rewards-header">
                <h2><FaTrophy /> نظام المكافآت</h2>
                <p>اجمع النقاط وافتح الإنجازات الجديدة!</p>
            </div>

            <div className="stats-overview">
                <div className="stat-card">
                    <div className="stat-icon">
                        <FaStar />
                    </div>
                    <div className="stat-info">
                        <h3>المستوى الحالي</h3>
                        <p>{getLevelIcon(level)} {getLevelTitle(level)} ({level})</p>
                        <div className="progress-bar">
                            <div 
                                className="progress-fill" 
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                        <small>{points % 100}/100 نقطة للمستوى التالي</small>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">
                        <FaCalculator />
                    </div>
                    <div className="stat-info">
                        <h3>إجمالي النقاط</h3>
                        <p>{points} نقطة</p>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">
                        <FaBook />
                    </div>
                    <div className="stat-info">
                        <h3>العمليات المكتملة</h3>
                        <p>{stats.calculations} عملية</p>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon">
                        <FaFire />
                    </div>
                    <div className="stat-info">
                        <h3>التحدي اليومي</h3>
                        <p>{stats.dailyStreak} يوم متتالي</p>
                    </div>
                </div>
            </div>

            <div className="achievements-section">
                <h3>الإنجازات</h3>
                <div className="achievements-grid">
                    {allAchievements.map(achievement => {
                        const isUnlocked = achievements.some(a => a.id === achievement.id);
                        const isAvailable = achievement.condition(stats);

                        return (
                                                         <div 
                                 key={achievement.id} 
                                 className={`achievement-card ${isUnlocked ? 'unlocked' : isAvailable ? 'available' : 'locked'}`}
                             >
                                 <div className="achievement-icon">
                                     {getAchievementIcon(achievement.iconType)}
                                 </div>
                                 <div className="achievement-info">
                                     <h4>{achievement.title}</h4>
                                     <p>{achievement.description}</p>
                                     <span className="achievement-points">+{achievement.points} نقطة</span>
                                 </div>
                                 {isUnlocked && (
                                     <div className="achievement-badge">
                                         <FaAward />
                                     </div>
                                 )}
                             </div>
                        );
                    })}
                </div>
            </div>

            <div className="rewards-tips">
                <h3>نصائح لجمع المزيد من النقاط</h3>
                <ul>
                    <li>استخدم الحاسبة يومياً لزيادة التحدي اليومي</li>
                    <li>جرب العمليات العلمية المعقدة للحصول على نقاط أكثر</li>
                    <li>أكمل العمليات بسرعة للحصول على إنجاز "سريع كالبرق"</li>
                    <li>استمر في التعلم لرفع مستواك وفتح إنجازات جديدة</li>
                </ul>
            </div>
        </div>
    );
};

export default RewardsSystem; 